import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import debounce from "lodash.debounce";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Alert, Badge, Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DropdownAtom from "../../../../components/atoms/dropdown/dropdown";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";
import { setting } from "../../../../store/services";
import {
	useLazyLookupUserByEmailQuery,
	useLazySearchUsersByEmailQuery,
} from "../../../../store/services/projectMembers";
import { showError } from "../../../../utils/method";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USER_SOURCE = "external";
const SUGGEST_MIN_LENGTH = 2;
const SUGGEST_LIMIT = 8;

const isValidEmail = (value) => EMAIL_PATTERN.test(value);

const getErrorStatus = (error) =>
	error?.status ?? error?.statusCode ?? error?.data?.statusCode;

const toLookedUpUser = (user) => ({
	id: user.id,
	firstName: user.firstName,
	lastName: user.lastName,
	email: user.email,
	isProjectLocal: Boolean(user.isProjectLocal),
});

const isValidPhoneNumber = (phone) => {
	if (!phone || phone.trim() === "") {
		return true;
	}
	const phoneRegex =
		/^(\+?\d{1,3}[-\s]?)?\(?\d{3,4}\)?[-\s]?\d{3,4}[-\s]?\d{3,4}$/;
	return phoneRegex.test(phone.trim());
};

function TabExternalGuest({
	existingMemberIds = [],
	selectedUsers,
	onSelectedUsersChange,
}) {
	const { t } = useTranslation();
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [company, setCompany] = useState("");
	const [lookupState, setLookupState] = useState("idle");
	const [lookedUpUser, setLookedUpUser] = useState(null);
	const [lookupErrorKey, setLookupErrorKey] = useState("");
	const [errorObj, setErrorObj] = useState({});
	const [companyOptions, setCompanyOptions] = useState([]);
	const [searchResults, setSearchResults] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [highlightIndex, setHighlightIndex] = useState(-1);

	const lookupRequestIdRef = useRef(0);
	const suggestRef = useRef(null);
	const skipSuggestRef = useRef(false);

	const [triggerLookup] = useLazyLookupUserByEmailQuery();
	const [triggerSearch] = useLazySearchUsersByEmailQuery();

	const excludedEmails = useMemo(
		() =>
			new Set(
				selectedUsers
					.filter((u) => u.source === USER_SOURCE)
					.map((u) => u.email),
			),
		[selectedUsers],
	);

	// A matched user who is already a project member, or already picked in this
	// session, can't be added again. We still SHOW them in the list (flagged +
	// non-selectable) rather than hiding them, so it's clear why - hiding made
	// the list look empty when the only match was an existing member.
	const getSuggestionDisabledReason = useCallback(
		(user) => {
			if (existingMemberIds.includes(user.id)) {
				return "projects.members.suggest-already-member";
			}
			if (excludedEmails.has(user.email?.toLowerCase())) {
				return "projects.members.suggest-already-selected";
			}
			return "";
		},
		[existingMemberIds, excludedEmails],
	);

	const suggestions = useMemo(() => {
		if (email.trim().length < SUGGEST_MIN_LENGTH) {
			return [];
		}
		// Email-matched server-side; show all (exclusions are flagged, not hidden).
		return searchResults.slice(0, SUGGEST_LIMIT);
	}, [email, searchResults]);

	useEffect(() => {
		const loadCompanies = async () => {
			try {
				const companyResult = await setting.getCompanies();
				setCompanyOptions(
					(companyResult.items || []).map((item) => ({
						label: item.name,
						value: item.id,
					})),
				);
			} catch (error) {
				showError(t, error);
			}
		};
		loadCompanies();
	}, [t]);

	const runLookup = useMemo(
		() =>
			debounce(async (value) => {
				if (!isValidEmail(value)) {
					setLookupState("idle");
					setLookedUpUser(null);
					setLookupErrorKey("");
					return;
				}

				const requestId = ++lookupRequestIdRef.current;
				setLookupState("loading");
				setLookedUpUser(null);
				setLookupErrorKey("");

				try {
					const user = await triggerLookup(value).unwrap();
					if (requestId !== lookupRequestIdRef.current) {
						return;
					}
					setLookedUpUser(toLookedUpUser(user));
					setFirstName(user.firstName || "");
					setLastName(user.lastName || "");
					setLookupState("found");
				} catch (error) {
					if (requestId !== lookupRequestIdRef.current) {
						return;
					}
					setLookedUpUser(null);
					if (getErrorStatus(error) === 404) {
						setLookupState("new");
						setLookupErrorKey("");
					} else {
						setLookupState("error");
						setLookupErrorKey(
							error?.originalMessage ||
								"projects.members.error-email-lookup-failed",
						);
					}
				}
			}, 300),
		[triggerLookup],
	);

	useEffect(() => () => runLookup.cancel(), [runLookup]);

	// Debounced typeahead: partial email search filtered in the DB.
	const runSuggestSearch = useMemo(
		() =>
			debounce(async (value) => {
				const query = value.trim();
				if (query.length < SUGGEST_MIN_LENGTH) {
					setSearchResults([]);
					return;
				}
				try {
					const res = await triggerSearch({
						search: query,
						limit: SUGGEST_LIMIT,
					}).unwrap();
					setSearchResults(res?.items ?? []);
				} catch {
					setSearchResults([]);
				}
			}, 250),
		[triggerSearch],
	);

	useEffect(() => () => runSuggestSearch.cancel(), [runSuggestSearch]);

	useEffect(() => {
		const onDocClick = (event) => {
			if (
				suggestRef.current &&
				!suggestRef.current.contains(event.target)
			) {
				setShowSuggestions(false);
				setHighlightIndex(-1);
			}
		};
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, []);

	const applyUserToForm = useCallback((user) => {
		setEmail(user.email || "");
		setFirstName(user.firstName || "");
		setLastName(user.lastName || "");
		setPhoneNumber(user.phoneNumber || "");
		setCompany(user.company?.id || "");
		setErrorObj({});
		setLookupErrorKey("");
	}, []);

	const resolveExistingUser = useCallback(
		(user) => {
			skipSuggestRef.current = true;
			applyUserToForm(user);
			setLookedUpUser(toLookedUpUser(user));
			setLookupState("found");
			setLookupErrorKey("");
			setShowSuggestions(false);
			setHighlightIndex(-1);
			runLookup.cancel();
		},
		[applyUserToForm, runLookup],
	);

	const selectSuggestion = useCallback(
		(user) => {
			// Existing members / already-selected users are shown but not pickable.
			if (getSuggestionDisabledReason(user)) {
				return;
			}
			// Suggestions always come from the server search, so they are real
			// users with an id - resolve them directly.
			resolveExistingUser(user);
		},
		[resolveExistingUser, getSuggestionDisabledReason],
	);

	const onEmailChange = (e) => {
		const value = e.target.value;
		setEmail(value);
		setErrorObj((prev) => ({ ...prev, email: "" }));
		setLookedUpUser(null);
		setLookupState("idle");
		setLookupErrorKey("");
		lookupRequestIdRef.current += 1;
		runLookup.cancel();
		runSuggestSearch.cancel();

		if (skipSuggestRef.current) {
			skipSuggestRef.current = false;
			return;
		}

		const trimmed = value.trim();
		if (!trimmed) {
			setSearchResults([]);
			setShowSuggestions(false);
			setHighlightIndex(-1);
			return;
		}

		setShowSuggestions(true);
		setHighlightIndex(-1);
		runSuggestSearch(trimmed);

		if (isValidEmail(trimmed)) {
			setLookupState("loading");
			runLookup(trimmed);
		}
	};

	const onEmailKeyDown = (e) => {
		if (!showSuggestions || suggestions.length === 0) {
			return;
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightIndex((prev) =>
				prev < suggestions.length - 1 ? prev + 1 : 0,
			);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightIndex((prev) =>
				prev > 0 ? prev - 1 : suggestions.length - 1,
			);
		} else if (e.key === "Enter" && highlightIndex >= 0) {
			e.preventDefault();
			selectSuggestion(suggestions[highlightIndex]);
		} else if (e.key === "Escape") {
			setShowSuggestions(false);
			setHighlightIndex(-1);
		}
	};

	const resetForm = () => {
		setFirstName("");
		setLastName("");
		setEmail("");
		setPhoneNumber("");
		setCompany("");
		setLookedUpUser(null);
		setLookupState("idle");
		setLookupErrorKey("");
		setErrorObj({});
		setShowSuggestions(false);
		setHighlightIndex(-1);
		setSearchResults([]);
		lookupRequestIdRef.current += 1;
		runLookup.cancel();
		runSuggestSearch.cancel();
	};

	const addToSelection = () => {
		const errs = {};
		const trimmedEmail = email.trim();

		if (!trimmedEmail) {
			errs.email = "users.error-email-required";
		} else if (!isValidEmail(trimmedEmail)) {
			errs.email = "users.error-invalid-email";
		}
		if (lookupState === "loading") {
			errs.email = "projects.members.error-email-lookup-pending";
		}
		if (lookupState === "new" || lookupState === "idle") {
			if (!firstName.trim()) {
				errs.firstName = "users.error-first-name-required";
			}
			if (!lastName.trim()) {
				errs.lastName = "users.error-last-name-required";
			}
		}
		if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
			errs.phoneNumber = "users.error-invalid-phone";
		}

		if (Object.keys(errs).length) {
			setErrorObj(errs);
			return;
		}

		const normalizedEmail = trimmedEmail.toLowerCase();
		if (selectedUsers.some((u) => u.email === normalizedEmail)) {
			setErrorObj({
				email: "projects.permissions.error-duplicate-email",
			});
			return;
		}

		onSelectedUsersChange([
			...selectedUsers,
			{
				email: normalizedEmail,
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				phoneNumber: phoneNumber.trim() || undefined,
				company: company || undefined,
				isExisting: lookupState === "found",
				source: USER_SOURCE,
			},
		]);
		resetForm();
	};

	const fieldsLocked = lookupState === "found";
	const showSuggestList =
		showSuggestions &&
		email.trim().length >= SUGGEST_MIN_LENGTH &&
		suggestions.length > 0;

	return (
		<div className="add-member-wizard__tab-panel">
			<div className="add-member-wizard__info-box">
				<FontAwesomeIcon icon={faCircleInfo} />
				<span>{t("projects.members.external-user-info")}</span>
			</div>

			<Row>
				<Col>
					<div
						className="add-member-wizard__email-suggest"
						ref={suggestRef}
					>
						<TextFieldAtom
							type="email"
							label={t("form.email")}
							placeholder={t("projects.members.email-search-placeholder")}
							required
							maxLength={50}
							value={email}
							error={errorObj.email ? t(errorObj.email) : ""}
							autoComplete="off"
							onChange={onEmailChange}
							onFocus={() => {
								if (email.trim().length >= SUGGEST_MIN_LENGTH) {
									setShowSuggestions(true);
								}
							}}
							onKeyDown={onEmailKeyDown}
						/>
						{showSuggestList && (
							<ul
								className="add-member-wizard__suggest-list"
								role="listbox"
							>
								{suggestions.map((user, index) => {
									const disabledReason =
										getSuggestionDisabledReason(user);
									const isDisabled = !!disabledReason;
									return (
										<li
											key={user.id}
											role="option"
											aria-selected={index === highlightIndex}
											aria-disabled={isDisabled}
											className={`${index === highlightIndex ? "active" : ""}${isDisabled ? " is-disabled" : ""}`}
											onMouseDown={(e) => {
												e.preventDefault();
												if (!isDisabled) {
													selectSuggestion(user);
												}
											}}
										>
											<span className="add-member-wizard__suggest-name">
												{user.firstName} {user.lastName}
											</span>
											<span className="add-member-wizard__suggest-email">
												{user.email}
											</span>
											{user.company?.name && (
												<span className="add-member-wizard__suggest-meta">
													{user.company.name}
												</span>
											)}
											{isDisabled && (
												<span className="add-member-wizard__suggest-badge">
													{t(disabledReason)}
												</span>
											)}
										</li>
									);
								})}
							</ul>
						)}
					</div>
					{lookupState === "loading" && (
						<p className="add-member-wizard__lookup-hint text-muted">
							{t("common.loading")}
						</p>
					)}
				</Col>
			</Row>

			{lookupState === "error" && (
				<Alert variant="danger" className="py-2">
					{t(lookupErrorKey, {
						defaultValue: t(
							"projects.members.error-email-lookup-failed",
						),
					})}
				</Alert>
			)}

			{lookupState === "found" && lookedUpUser && (
				<div className="add-member-wizard__lookup-result">
					<div className="add-member-wizard__lookup-result-header">
						<strong>
							{t("projects.members.user-found", {
								name:
									[
										lookedUpUser.firstName,
										lookedUpUser.lastName,
									]
										.filter(Boolean)
										.join(" ") || lookedUpUser.email,
							})}
						</strong>
						<Badge bg="success">
							{lookedUpUser.isProjectLocal
								? t("projects.members.user-type-project-local")
								: t("projects.members.user-type-global")}
						</Badge>
					</div>
					<p className="mb-1">
						{lookedUpUser.firstName} {lookedUpUser.lastName}
					</p>
					<p className="text-muted small mb-0">{lookedUpUser.email}</p>
				</div>
			)}

			{lookupState === "new" && email.trim() && (
				<Alert variant="info" className="py-2">
					{t("projects.members.user-new")}
				</Alert>
			)}

			<Row className="mt-3">
				<Col>
					<TextFieldAtom
						type="text"
						label={t("form.first-name")}
						placeholder={t("form.first-name")}
						required={lookupState === "new" || lookupState === "idle"}
						maxLength={50}
						value={firstName}
						disabled={fieldsLocked}
						error={
							errorObj.firstName
								? t("users.error-first-name-required")
								: ""
						}
						onChange={(e) => {
							setFirstName(e.target.value);
							setErrorObj((prev) => ({
								...prev,
								firstName: e.target.value
									? ""
									: "users.error-first-name-required",
							}));
						}}
					/>
				</Col>
				<Col>
					<TextFieldAtom
						type="text"
						label={t("form.last-name")}
						placeholder={t("form.last-name")}
						required={lookupState === "new" || lookupState === "idle"}
						maxLength={50}
						value={lastName}
						disabled={fieldsLocked}
						error={
							errorObj.lastName
								? t("users.error-last-name-required")
								: ""
						}
						onChange={(e) => {
							setLastName(e.target.value);
							setErrorObj((prev) => ({
								...prev,
								lastName: e.target.value
									? ""
									: "users.error-last-name-required",
							}));
						}}
					/>
				</Col>
			</Row>

			<Row className="mt-3">
				<Col>
					<TextFieldAtom
						type="tel"
						label={t("form.mobile-number")}
						placeholder={t("form.mobile-number")}
						value={phoneNumber}
						disabled={fieldsLocked}
						error={
							errorObj.phoneNumber ? t(errorObj.phoneNumber) : ""
						}
						onChange={(e) => {
							const phoneValue = e.target.value;
							setPhoneNumber(phoneValue);
							if (phoneValue && !isValidPhoneNumber(phoneValue)) {
								setErrorObj((prev) => ({
									...prev,
									phoneNumber: "users.error-invalid-phone",
								}));
							} else {
								setErrorObj((prev) => ({
									...prev,
									phoneNumber: "",
								}));
							}
						}}
						hint={t("users.mobile-number-hint")}
					/>
				</Col>
				<Col>
					<DropdownAtom
						label={t("users.company")}
						placeholder={t("users.company")}
						value={company}
						required={false}
						options={companyOptions}
						disabled={fieldsLocked}
						onChange={(e) => setCompany(e.target.value)}
					/>
				</Col>
			</Row>

			<Button
				variant="outline-primary"
				className="mt-3"
				onClick={addToSelection}
			>
				{t("projects.members.add-to-selection")}
			</Button>
		</div>
	);
}

export default TabExternalGuest;
